<template>
  <div class="BaseTemplateItemSetting q-pa-md q-pa-lg-lg scroll hide-scrollbar">
    <div v-if="templateStore.isMultiSelect" class="BaseTemplateItemSetting__multi-select column no-wrap gap-sm">
      <div class="row items-center justify-between">
        <span class="fs-13px fw-600 text-cool-gray-100"
          >{{ templateStore.selectedItemIds.length }} {{ t('multi_select.selected') }}</span
        >
        <k-icon
          name="close"
          size="18px"
          color="cool-gray-50"
          class="cursor-pointer"
          @click="templateStore.selectItem(null)"
        />
      </div>

      <k-divider size="sm" class="q-mb-sm" />

      <p class="q-mb-xs fs-11px fw-500 text-cool-gray-70">{{ t('multi_select.alignment') }}</p>
      <div class="row q-col-gutter-sm">
        <div class="col-6">
          <div class="BaseTemplateItemSetting__action gap-sm items-center justify-between">
            <k-icon
              name="vertical-top"
              class="cursor-pointer"
              color="cool-gray-70"
              @click="templateStore.alignItems('top')"
            />
            <k-icon
              name="vertical-middle"
              class="cursor-pointer"
              color="cool-gray-70"
              @click="templateStore.alignItems('center-v')"
            />
            <k-icon
              name="vertical-bottom"
              class="cursor-pointer"
              color="cool-gray-70"
              @click="templateStore.alignItems('bottom')"
            />
          </div>
        </div>
        <div class="col-6">
          <div class="BaseTemplateItemSetting__action gap-sm items-center justify-between">
            <k-icon
              name="horizontal-bottom"
              class="cursor-pointer"
              color="cool-gray-70"
              @click="templateStore.alignItems('left')"
            />
            <k-icon
              name="horizontal-middle"
              class="cursor-pointer"
              color="cool-gray-70"
              @click="templateStore.alignItems('center-h')"
            />
            <k-icon
              name="horizontal-top"
              class="cursor-pointer"
              color="cool-gray-70"
              @click="templateStore.alignItems('right')"
            />
          </div>
        </div>
      </div>

      <k-divider size="sm" class="q-my-sm" />

      <p class="q-mb-xs fs-11px fw-500 text-cool-gray-70">{{ t('multi_select.rotation') }}</p>
      <div class="row items-center gap-sm">
        <k-input
          v-model.number="rotationValue"
          v-number
          small
          :has-clear-icon="false"
          type="number"
          style="max-width: 80px"
          input-class="text-center"
          @update:model-value="onRotationUpdate"
        />
        <span class="fs-11px fw-500 text-cool-gray-50">{{ t('multi_select.deg') }}</span>
        <span class="BaseTemplateItemSetting__action cursor-pointer" style="padding: 6px" @click="onRotate">
          <k-icon name="rotate" size="18px" color="cool-gray-70" />
        </span>
      </div>

      <k-divider size="sm" class="q-my-sm" />

      <p class="q-mb-none fs-11px fw-500 text-cool-gray-70">{{ t('multi_select.gap') }}</p>
      <div class="row items-center gap-sm">
        <k-input
          v-model="localGap"
          v-number
          small
          :has-clear-icon="false"
          type="number"
          style="max-width: 80px"
          input-class="text-center"
        />
        <span class="fs-11px fw-500 text-cool-gray-50">px</span>
        <div class="row gap-xs">
          <div
            class="BaseTemplateItemSetting__action cursor-pointer"
            style="padding: 6px"
            :title="t('multi_select.distribute_horizontally')"
            @click="templateStore.distributeWithGap(Number(localGap), 'horizontal')"
          >
            <k-icon name="vertical-middle" size="18px" color="cool-gray-70" />
          </div>
          <div
            class="BaseTemplateItemSetting__action cursor-pointer"
            style="padding: 6px"
            :title="t('multi_select.distribute_vertically')"
            @click="templateStore.distributeWithGap(Number(localGap), 'vertical')"
          >
            <k-icon name="horizontal-middle" size="18px" color="cool-gray-70" />
          </div>
        </div>
      </div>

      <k-divider size="sm" class="q-my-sm" />

      <div class="row gap-sm">
        <k-action-btn
          class="flex-1"
          size="sm"
          :label="t('multi_select.duplicate')"
          @click="templateStore.duplicateSelectedItems()"
        />
        <k-action-btn
          class="BaseTemplateItemSetting__delete-btn flex-1"
          size="sm"
          :label="t('multi_select.delete')"
          @click="showDeleteConfirm = true"
        />
      </div>
    </div>

    <template v-else-if="templateStore.activeItem.id">
      <BaseTemplateItemTextSetting v-if="templateStore.activeItem.type === 'text'" />
      <BaseTemplateItemImageSetting v-if="templateStore.activeItem.type === 'image'" />
      <BaseTemplateItemShapeSetting v-if="templateStore.activeItem.type === 'shape'" />
    </template>

    <BasePopup
      :model-value="showDeleteConfirm"
      :text="t('multi_select.delete_title')"
      icon="trash"
      :has-warning="false"
      is-delete-modal
      :right-button-label="t('layers.delete.yes')"
      right-button-event="delete"
      @update:model-value="!$event && (showDeleteConfirm = false)"
      @delete="handleDeleteItem"
      @cancel="showDeleteConfirm = false"
    />
  </div>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue'
import { useTemplateStore } from 'src/stores'
import translate from 'src/common/helpers/language/translate'
import translationObject from '../../translate/baseTemplate.translate'
import BaseTemplateItemTextSetting from './BaseTemplateItemTextSetting.vue'
import BaseTemplateItemImageSetting from './BaseTemplateItemImageSetting.vue'
import BaseTemplateItemShapeSetting from './BaseTemplateItemShapeSetting.vue'

defineOptions({ name: 'BaseTemplateItemSetting' })

const { t } = translate(translationObject)
const templateStore = useTemplateStore()

const localGap = ref(0)
const rotationValue = ref(0)
const showDeleteConfirm = ref(false)

watch(
  () => templateStore.isMultiSelect,
  (isMulti) => {
    if (!isMulti) {
      localGap.value = 0
      rotationValue.value = 0
    }
  },
)

async function onRotationUpdate() {
  await nextTick()
  const clamped = ((Math.round(rotationValue.value) % 360) + 360) % 360
  rotationValue.value = clamped
  templateStore.rotateSelectedItems(clamped)
}

function onRotate() {
  rotationValue.value = (rotationValue.value + 90) % 360
  templateStore.rotateSelectedItems(rotationValue.value)
}

function handleDeleteItem() {
  if (showDeleteConfirm.value) templateStore.deleteSelectedItems()
  showDeleteConfirm.value = false
}
</script>

<style lang="sass" scoped>
.BaseTemplateItemSetting
  &__multi-select
    padding-top: 4px

  &__action
    outline: 1.5px solid var(--cool-gray-40)
    border-radius: 8px
    padding: 8px
    display: flex

  &__delete-btn
    border-color: var(--error-100) !important
    color: var(--error-100) !important
    &:hover
      :deep(.q-focus-helper)
        background: var(--white) !important
</style>
